import { API } from 'aws-amplify';
import { t } from 'i18next';
import { useState, useContext, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';

import { enrollmentChangeTypes } from '../../../utils/enums';
import { ProtectedRouteContext } from '../../Context';
import Button from '../../shared/Button';
import View from '../../shared/View';
import LabeledSelect from '../../shared/LabeledSelect';
import DatePicker from '../../shared/datePick';
import RenderIf from '../../renderIf';
import { RequiredMarker } from '../../shared/muiControls';

import useEnrollmentModal from './enrollmentModal/useEnrollmentModal';

import './EnrollmentModal.scss';

function EnrollmentModal(props) {
    const { userSettings } = useContext(ProtectedRouteContext);
    const { cancelFunction, onStatusChange, enrollID, appInstId, type } = props;

    const [initialized, setInitialized] = useState(false);
    const [loading, setLoading] = useState(true);
    const [doingAjax, setDoingAjax] = useState(true);
    const [reason, setReason] = useState(0);
    const [exitSchool, setExitSchool] = useState(-1);
    const [entrySchool, setEntrySchool] = useState(-1);
    const [readOnly, setReadOnly] = useState('');
    const [enrollmentOptions, setEnrollmentOptions] = useState([]);
    const [schoolOptions, setSchoolOptions] = useState([]);
    const entrySchoolRef = useRef();

    const typeEnum = useMemo(() => {
        return { Add: 1, Edit: 2, View: 3, Delete: 4 };
    }, []);

    const _cancel = useMemo(() => {
        return typeof cancelFunction === 'function' ? cancelFunction : null;
    }, [cancelFunction]);

    const {
        exitDate,
        entryDate,
        show_entryDateError,
        show_exitDateError,
        errors,
        dateLabelSx,
        dateTextFieldSx,
        initDates,
        resetDates,
        handleDateChange,
        handleDateBlur,
        handleDateError,
    } = useEnrollmentModal();

    const _changeStatus = async () => {
        setDoingAjax(true);
        if (type !== typeEnum.Delete) {
            switch (reason) {
                case enrollmentChangeTypes.Transfer:
                    if (errors?.length === 0 && parseInt(entrySchoolRef.current?.value) !== -1) {
                        try {
                            await API.patch('ShipyardAPI', `/application/${appInstId}/enrollment_change`, {
                                queryStringParameters: {
                                    enrollID: enrollID,
                                    reasonId: reason,
                                    entryDate: entryDate.toISOString(),
                                    enteringSchoolId: entrySchoolRef.current.value,
                                    exitingSchoolId: exitSchool,
                                    exitDate: exitDate.toISOString(),
                                },
                            });

                            onStatusChange();
                        } catch (e) {
                            toast.error('General.AjaxError');
                            setDoingAjax(false);
                        }
                    } else {
                        toast.error(t('General.IncompleteFields'));
                        setDoingAjax(false);
                    }
                    break;
                case enrollmentChangeTypes.Withdrawal:
                    if (errors?.find?.((err) => err?.key === 'exitDate')?.exitDateError !== true) {
                        try {
                            await API.patch('ShipyardAPI', `/application/${appInstId}/enrollment_change`, {
                                queryStringParameters: {
                                    enrollID: enrollID,
                                    reasonId: reason,
                                    exitingSchoolId: exitSchool,
                                    exitDate: exitDate.toISOString(),
                                },
                            });

                            toast.success(t('General.ChangesSaved'));

                            onStatusChange();
                        } catch (e) {
                            toast.error('Ajax error');
                            setDoingAjax(false);
                        }
                    } else {
                        toast.error(t('General.IncompleteFields'));
                        setDoingAjax(false);
                    }
                    break;
                default:
                    toast.error(t('General.InvalidOption'));
                    setDoingAjax(false);
                    break;
            }
        } else {
            try {
                await API.del('ShipyardAPI', `/application/${appInstId}/enrollment_change`, {
                    queryStringParameters: {
                        enrollID: enrollID,
                    },
                });

                onStatusChange();
            } catch (e) {
                toast.error('General.AjaxError');
                setDoingAjax(false);
            }
        }
    };

    const enrollmentOptionsJSX = useMemo(() => {
        return [
            <option
                key="option-0"
                value="-1"
            ></option>,
            ...enrollmentOptions.map((ent) => {
                return (
                    <option
                        key={`option-${ent.ChangeReasonID}`}
                        value={ent.ChangeReasonID}
                        disabled={ent.IsEdit === 0 ? true : null}
                        hidden={ent.IsEdit === 0 ? true : null}
                    >
                        {ent.ChangeReason}
                    </option>
                );
            }),
        ];
    }, [enrollmentOptions]);

    const schoolOptionsJSX = useMemo(() => {
        return [
            <option
                key="option-0"
                value="-1"
            ></option>,
            ...schoolOptions.map((ent) => {
                return (
                    <option
                        key={`option-${ent.id}`}
                        value={ent.id}
                        disabled={ent?.isDisplayOnly === 1}
                    >
                        {ent.text[userSettings.LanguagePreference]}
                    </option>
                );
            }),
        ];
    }, [schoolOptions, userSettings]);

    const mainJSX = useMemo(() => {
        return !loading ? (
            <>
                <p>
                    {type === typeEnum.Add ? (
                        t('EnrollmentChange.EnrollmentModal.AddEnrollmentText')
                    ) : type === typeEnum.Edit ? (
                        t('EnrollmentChange.EnrollmentModal.EditEnrollmentText')
                    ) : type === typeEnum.Delete ? (
                        <ReactMarkdown
                            disallowedElements={['p']}
                            unwrapDisallowed={true}
                        >
                            {t('EnrollmentChange.EnrollmentModal.DeleteEnrollmentText')}
                        </ReactMarkdown>
                    ) : (
                        t('EnrollmentChange.EnrollmentModal.ViewEnrollmentText')
                    )}
                </p>

                <LabeledSelect
                    className="reason-select"
                    labelText={`${t('EnrollmentChange.EnrollmentModal.ChangeReasonLabel')}:`}
                    required={enrollID !== undefined && !readOnly}
                    value={reason}
                    onChange={(e) => {
                        setReason(parseInt(e.target.value));
                        resetDates();
                    }}
                    disabled={enrollID !== undefined || readOnly}
                >
                    {enrollmentOptionsJSX}
                </LabeledSelect>
            </>
        ) : (
            `${t('General.Loading')}...`
        );
    }, [loading, reason, enrollmentOptionsJSX, readOnly, enrollID, type, typeEnum]);

    const titleJSX = useMemo(() => {
        return (
            <>
                {type === typeEnum.Add
                    ? t('EnrollmentChange.EnrollmentModal.AddEnrollmentTitle')
                    : type === typeEnum.Edit
                      ? t('EnrollmentChange.EnrollmentModal.EditEnrollmentTitle')
                      : type === typeEnum.Delete
                        ? t('EnrollmentChange.EnrollmentModal.DeleteEnrollmentTitle')
                        : t('EnrollmentChange.EnrollmentModal.ViewEnrollmentTitle')}
            </>
        );
    }, [type, typeEnum]);

    const buttonJSX = useMemo(() => {
        return !loading ? (
            <>
                <Button
                    useMui={true}
                    type="secondary"
                    onClick={_cancel}
                >
                    {t('General.Cancel')}
                </Button>

                {type === typeEnum.Edit || type === typeEnum.Add ? (
                    <Button
                        useMui={true}
                        onClick={_changeStatus}
                        interactive={false}
                    >
                        {t('General.Save')}
                    </Button>
                ) : null}

                {type === typeEnum.Delete ? (
                    <Button
                        useMui={true}
                        className="delete-button"
                        onClick={_changeStatus}
                        interactive={false}
                    >
                        {t('General.Delete')}
                    </Button>
                ) : null}
            </>
        ) : null;
    }, [loading, _cancel, _changeStatus, type, typeEnum]);

    useEffect(() => {
        async function getHistoryEntry() {
            setLoading(true);
            setDoingAjax(true);

            let _response = await API.get('ShipyardAPI', `/application/${appInstId}/enrollment_change`, [
                {
                    queryStringParameters: {
                        enrollID,
                    },
                },
            ]);

            setEnrollmentOptions(_response.enrollmentOptions);

            setSchoolOptions(_response.schoolOptions);

            if (enrollID) {
                _response = await API.get('ShipyardAPI', `/application/${appInstId}/enrollment_history`, {
                    queryStringParameters: {
                        enrollID: enrollID,
                    },
                });

                let [_historyData] = _response.enrollmentHistory;

                setEntrySchool(_historyData.EnteringSchoolID ? _historyData.EnteringSchoolID : undefined);

                setExitSchool(_historyData.ExitingSchoolID ? _historyData.ExitingSchoolID : undefined);

                initDates(_historyData.ExitDate ? _historyData.ExitDate : null, _historyData.EntryDate ? _historyData.EntryDate : null);

                let readOnlyTypes = [
                    enrollmentChangeTypes.Initial,
                    enrollmentChangeTypes.Renewal,
                    enrollmentChangeTypes.ImportInitial,
                    enrollmentChangeTypes.ImportRenewal,
                ].includes(_historyData.EnrollmentChangeReasonID);

                setReadOnly(_historyData.LatestChangeRecord === 1 && !readOnlyTypes && type !== typeEnum.Delete ? false : true);

                setReason(_historyData.EnrollmentChangeReasonID);
            } else {
                setExitSchool(_response.currentSchool.CurrentSchoolOptionID);

                setReadOnly(false);
            }

            setLoading(false);

            setDoingAjax(false);

            setInitialized(true);
        }

        if (!initialized) getHistoryEntry();
    }, [enrollID, appInstId, initialized, type, typeEnum]);

    return (
        <View
            doingAjax={doingAjax}
            className="enrollment-change-modal"
            name="enrollment-change-modal"
        >
            <Dialog open={true}>
                <DialogTitle>{titleJSX}</DialogTitle>
                <DialogContent>
                    <Box>
                        {mainJSX}

                        <RenderIf isTrue={!loading}>
                            <RenderIf isTrue={reason !== 0}>
                                <div
                                    className="row"
                                    key="exit"
                                >
                                    <LabeledSelect
                                        labelText={`${t('EnrollmentChange.EnrollmentModal.ExitSchoolLabel')}`}
                                        disabled={true}
                                        value={exitSchool}
                                    >
                                        {schoolOptionsJSX}
                                    </LabeledSelect>

                                    <Box>
                                        <Typography sx={dateLabelSx}>
                                            <RequiredMarker /> {t('EnrollmentChange.EnrollmentModal.ExitDateLabel')}:
                                        </Typography>
                                        <DatePicker
                                            disabled={readOnly || doingAjax}
                                            value={exitDate}
                                            onChange={(val) => handleDateChange(val, 'exitDate')}
                                            onBlur={(event) => {
                                                if (event?.type === 'blur') {
                                                    handleDateBlur('exitDate');
                                                } else {
                                                    //Called when a date is selected from the calendar, only valid dates are enabled.
                                                    handleDateError(null, event, 'exitDate');
                                                }
                                            }}
                                            onError={(err, val) => handleDateError(err, val, 'exitDate')}
                                            error={
                                                show_exitDateError &&
                                                errors?.find?.((err) => err?.key === 'exitDate')?.exitDateError === true
                                            }
                                            size="small"
                                            textFieldSx={dateTextFieldSx}
                                            clearable
                                        />
                                        <RenderIf
                                            isTrue={
                                                show_exitDateError &&
                                                errors?.find?.((err) => err?.key === 'exitDate' && err?.exitDateErrorMessage !== null)
                                            }
                                        >
                                            <Typography
                                                sx={(theme) => {
                                                    return {
                                                        fontSize: theme.spacing(1.35),
                                                        color: theme.palette.error.main,
                                                    };
                                                }}
                                            >
                                                {errors?.find?.((err) => err?.key === 'exitDate')?.exitDateErrorMessage}
                                            </Typography>
                                        </RenderIf>
                                    </Box>
                                </div>
                            </RenderIf>

                            <RenderIf
                                isTrue={[
                                    enrollmentChangeTypes.Initial,
                                    enrollmentChangeTypes.Renewal,
                                    enrollmentChangeTypes.Transfer,
                                    enrollmentChangeTypes.ImportInitial,
                                    enrollmentChangeTypes.ImportRenewal,
                                ].includes(reason)}
                            >
                                <div
                                    className="row"
                                    key="entry"
                                >
                                    <LabeledSelect
                                        labelText={`${t('EnrollmentChange.EnrollmentModal.EnteringSchoolLabel')}:`}
                                        required={!readOnly}
                                        disabled={readOnly}
                                        value={entrySchool}
                                        ref={entrySchoolRef}
                                    >
                                        {schoolOptionsJSX}
                                    </LabeledSelect>

                                    <Box>
                                        <Typography
                                            sx={(theme) => {
                                                return {
                                                    ...dateLabelSx(
                                                        theme,
                                                        show_entryDateError &&
                                                            errors?.find?.((err) => err?.key === 'entryDate')?.entryDateError === true
                                                    ),
                                                    mt: (theme) => theme.spacing(1.5),
                                                };
                                            }}
                                        >
                                            <RequiredMarker /> {t('EnrollmentChange.EnrollmentModal.EntryDateLabel')}:
                                        </Typography>
                                        <DatePicker
                                            disabled={readOnly || doingAjax}
                                            value={entryDate}
                                            onChange={(val) => handleDateChange(val, 'entryDate')}
                                            onBlur={(event) => {
                                                if (event?.type === 'blur') {
                                                    handleDateBlur('entryDate');
                                                } else {
                                                    //Called when a date is selected from the calendar, only valid dates are enabled.
                                                    handleDateError(null, event, 'entryDate');
                                                }
                                            }}
                                            onError={(err, val) => handleDateError(err, val, 'entryDate')}
                                            error={
                                                show_entryDateError &&
                                                errors?.find?.((err) => err?.key === 'entryDate')?.entryDateError === true
                                            }
                                            size="small"
                                            textFieldSx={dateTextFieldSx}
                                            clearable
                                        />
                                        <RenderIf
                                            isTrue={
                                                show_entryDateError &&
                                                errors?.find?.((err) => err?.key === 'entryDate' && err?.entryDateErrorMessage !== null)
                                            }
                                        >
                                            <Typography
                                                sx={(theme) => {
                                                    return {
                                                        fontSize: theme.spacing(1.35),
                                                        color: theme.palette.error.main,
                                                    };
                                                }}
                                            >
                                                {errors?.find?.((err) => err?.key === 'entryDate')?.entryDateErrorMessage}
                                            </Typography>
                                        </RenderIf>
                                    </Box>
                                </div>
                            </RenderIf>
                        </RenderIf>
                    </Box>
                    <Box>{buttonJSX}</Box>
                </DialogContent>
            </Dialog>
        </View>
    );
}

export default EnrollmentModal;
